import { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Input, Row, Select, Space, Upload, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAppAuth } from "../../../context/use-app-auth";
import requets from "../../../api";
import { uploadEntityImage } from "../../../api/service/cdn/cdn";
import MapSelector from "../../device/components/MapSelector";

const normalizeUploadFile = (e) => (Array.isArray(e) ? e : e?.fileList);

const buildUploadValue = (url, name) =>
  url ? [{ uid: `-${name}`, name, status: "done", url }] : [];

export default function FormEdit({ editLocation, departament, onSaveSuccess, onCancel }) {
  const { t } = useTranslation("global");
  const auth = useAppAuth();
  const req = useMemo(() => requets(auth.user?.access_token), [auth.user?.access_token]);
  const [form] = Form.useForm();
  const [messageSend, contextHolder] = message.useMessage();
  const [devices, setDevices] = useState([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [counties, setCounties] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // 1. Load devices once on mount so options are ready before form is populated
  useEffect(() => {
    req
      .get("/device", { limit: 1000 })
      .then((res) => {
        const arr = Array.isArray(res.data?.data) ? res.data.data : [];
        setDevices(arr.map((d) => ({ label: d.name, value: d.devEui, key: d.devEui })));
      })
      .catch(() => {})
      .finally(() => setDevicesLoaded(true));
  }, [req]);

  // 2. Populate form only after devices are in state (so Select can resolve labels)
  useEffect(() => {
    if (!devicesLoaded) return;

    if (!editLocation) {
      form.resetFields();
      setCounties([]);
      return;
    }

    const populate = async () => {
      const departmentUuid =
        editLocation.uuidDepartment ??
        editLocation.fkDepartmentUuid ??
        editLocation.department?.uuid;

      const countyUuid =
        editLocation.uuidCounty ??
        editLocation.fkCountyUuid ??
        editLocation.county?.uuid;

      // Extract devEuis as plain strings regardless of API shape
      const rawDevEuis = Array.isArray(editLocation.devEuis)
        ? editLocation.devEuis
        : (editLocation.devices ?? []).map((d) => d.devEui ?? d);
      const devEuis = rawDevEuis
        .map((v) => (typeof v === "object" ? v?.devEui : v))
        .filter((v) => typeof v === "string" && v.length > 0);

      const rawCoords = editLocation.coordenates ?? [];
      const coords =
        Array.isArray(rawCoords) && rawCoords.length === 2
          ? rawCoords.map(Number).filter(isFinite)
          : [];

      if (departmentUuid) {
        try {
          const res = await req.get(`/county/department/${departmentUuid}`);
          const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
          setCounties(raw.map((c) => ({ value: c.value ?? c.uuid, label: c.label ?? c.name })));
        } catch {
          setCounties([]);
        }
      }

      form.setFieldsValue({
        name: editLocation.name,
        description: editLocation.description,
        uuidDepartment: departmentUuid,
        uuidCounty: countyUuid,
        coordenates: coords.length === 2 ? coords : undefined,
        devEuis,
        singleLineDiagram: buildUploadValue(editLocation.urlDiagramImage, "diagrama-unifilar"),
        storeImage: buildUploadValue(editLocation.urlLocationImage, "imagen-tienda"),
      });
    };

    populate();
  }, [editLocation, devicesLoaded, form, req]);

  const onChangeDepartament = async (value) => {
    form.setFieldsValue({ uuidCounty: undefined });
    try {
      const res = await req.get(`/county/department/${value}`);
      const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setCounties(raw.map((c) => ({ value: c.value ?? c.uuid, label: c.label ?? c.name })));
    } catch {
      setCounties([]);
    }
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const diagramFile = values.singleLineDiagram?.[0];
      const storeImageFile = values.storeImage?.[0];

      // Keep existing URLs as fallback (already-uploaded files have .url, new ones have .originFileObj)
      let urlDiagramImage = diagramFile?.url ?? editLocation?.urlDiagramImage;
      let urlLocationImage = storeImageFile?.url ?? editLocation?.urlLocationImage;

      const payload = {
        name: values.name,
        description: values.description,
        uuidOrg: "b2211314-bbbb-4999-bc12-fe3cc5f19719",
        uuidCountry: "f0f68ba3-9d89-4f96-ba5c-2eeb08ad1aa2",
        uuidDepartment: values.uuidDepartment,
        uuidCounty: values.uuidCounty,
        coordenates: (values.coordenates ?? []).map(String),
        devEuis: (values.devEuis ?? [])
          .map((v) => (typeof v === "object" ? v?.devEui ?? v?.value : v))
          .filter((v) => typeof v === "string" && v.length > 0),
        ...(urlDiagramImage && { urlDiagramImage }),
        ...(urlLocationImage && { urlLocationImage }),
      };

      if (editLocation?.uuid) {
        await req.put(`/locations/${editLocation.uuid}`, payload);
        // Upload images via dedicated endpoints — backend proxies to CDN and updates the record
        if (diagramFile?.originFileObj) {
          await uploadEntityImage(req, `/locations/${editLocation.uuid}/diagram-image`, diagramFile.originFileObj);
        }
        if (storeImageFile?.originFileObj) {
          await uploadEntityImage(req, `/locations/${editLocation.uuid}/location-image`, storeImageFile.originFileObj);
        }
        messageSend.success(t("zones.sucessUpdated"));
      } else {
        const created = await req.post("/locations", payload);
        const newUuid = created.data?.uuid;
        if (newUuid) {
          if (diagramFile?.originFileObj) {
            await uploadEntityImage(req, `/locations/${newUuid}/diagram-image`, diagramFile.originFileObj);
          }
          if (storeImageFile?.originFileObj) {
            await uploadEntityImage(req, `/locations/${newUuid}/location-image`, storeImageFile.originFileObj);
          }
        }
        messageSend.success(t("zones.sucessCreated"));
      }

      onSaveSuccess?.();
    } catch (error) {
      messageSend.error(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="name"
              label={t("zones.name")}
              rules={[{ required: true, message: t("formValidator.mandatory") }]}
            >
              <Input placeholder={t("zones.placeHolderName")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="description"
              label={t("zones.description")}
              rules={[{ required: true, message: t("formValidator.mandatory") }]}
            >
              <Input placeholder={t("zones.placeHolderDescription")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item
              label={t("zones.department")}
              name="uuidDepartment"
              rules={[{ required: true, message: t("formValidator.mandatory") }]}
            >
              <Select
                size="large"
                placeholder={t("zones.placeHolderDepartmentName")}
                onChange={onChangeDepartament}
                options={departament}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item
              label={t("zones.municipality")}
              name="uuidCounty"
              rules={[{ required: true, message: t("formValidator.mandatory") }]}
            >
              <Select
                size="large"
                placeholder={t("zones.placeHolderMunicipality")}
                options={counties}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label={t("zones.device")}
              name="devEuis"
              rules={[{ required: true, message: t("formValidator.mandatory") }]}
            >
              <Select
                mode="multiple"
                size="large"
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                placeholder={t("zones.placeHolderDevice")}
                options={devices}
                loading={!devicesLoaded}
                notFoundContent={null}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xs={24} sm={12} md={6} lg={5}>
            <Form.Item
              label={t("zones.unifilarDiagram") || "Diagrama unifilar"}
              name="singleLineDiagram"
              valuePropName="fileList"
              getValueFromEvent={normalizeUploadFile}
            >
              <Upload listType="picture-card" maxCount={1} beforeUpload={() => false} accept=".jpg,.jpeg,.png,.webp">
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t("products.uploadButton") || "Cargar"}</div>
                </div>
              </Upload>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} lg={5}>
            <Form.Item
              label={t("zones.storeImage") || "Imagen de la tienda"}
              name="storeImage"
              valuePropName="fileList"
              getValueFromEvent={normalizeUploadFile}
            >
              <Upload listType="picture-card" maxCount={1} beforeUpload={() => false} accept=".jpg,.jpeg,.png,.webp">
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t("products.uploadButton") || "Cargar"}</div>
                </div>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xs={24}>
            <Form.Item
              label={t("deviceNew.coordinates") || "Ubicación en el mapa"}
              name="coordenates"
              rules={[{ required: true, message: t("formValidator.mandatory") }]}
              getValueFromEvent={(v) => v}
            >
              <MapSelector />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
          <Space>
            <Button shape="round" htmlType="submit" type="primary" loading={submitting}>
              {t("deviceEdit.saveChange")}
            </Button>
            <Button shape="round" onClick={onCancel}>
              {t("zones.cancel") || "Cancelar"}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </>
  );
}
